import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import coreDarkLogo from '../../assets/core-dark.jpg';
import {
  LoginContainer,
  LoginCard,
  LogoSection,
  NodeInfo,
  NodeLabel,
  NodeAddress,
  FormSection,
  SectionTitle,
  SectionIcon,
  StyledTextField,
  LoginButton,
  SecondaryButton,
  ButtonGroup,
  SecurityNote,
  SecurityIcon,
  SecurityText,
} from './styles';

interface LoginScreenProps {
  initialNodeAddress?: string;
  onLogin: (nodeAddress: string, clientId: string, clientSecret: string) => void;
  loading?: boolean;
  isSecure?: boolean;
}

const LoginScreen = observer(({ 
  initialNodeAddress = '',
  onLogin, 
  loading = false,
  isSecure = false
}: LoginScreenProps) => {
  const [nodeAddress, setNodeAddress] = useState(initialNodeAddress);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [step, setStep] = useState<'network' | 'credentials'>('network');

  const handleNetworkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (nodeAddress.trim()) {
      setStep('credentials');
    }
  };

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (clientId.trim() && clientSecret.trim() && nodeAddress.trim()) {
      onLogin(nodeAddress.trim(), clientId.trim(), clientSecret.trim());
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (isSecure) {
      const blocked = [
        'F5', 'F11', 'F12', 'Tab', 'Alt', 'Escape',
        e.ctrlKey && e.key === 'r',
        e.ctrlKey && e.shiftKey && e.key === 'I',
        e.ctrlKey && e.key === 'u',
        e.ctrlKey && e.key === 'w',
        e.altKey && e.key === 'F4'
      ];
      if (blocked.some(b => b === true || b === e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  useEffect(() => {
    if (isSecure) {
      const handleContextMenu = (e: MouseEvent) => e.preventDefault();
      const handleSelectStart = (e: Event) => e.preventDefault();
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
      };

      document.body.style.userSelect = 'none';
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('selectstart', handleSelectStart);
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('selectstart', handleSelectStart);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.body.style.userSelect = '';
      };
    }
  }, [isSecure]);

  return (
    <LoginContainer>
      <LoginCard elevation={0}>
        <LogoSection>
          <img src={coreDarkLogo} alt="Core Blockchain" style={{ width: '120px', marginBottom: '16px' }} />
        </LogoSection>

        {step === 'network' && (
          <>
            <NodeInfo>
              <NodeLabel variant="body2">Endereço da Rede</NodeLabel>
              <NodeAddress variant="body2">Configure o endereço do node</NodeAddress>
            </NodeInfo>

            <FormSection>
              <SectionTitle variant="h6">
                <SectionIcon className="bx bx-server" />
                Configuração de Rede
              </SectionTitle>
              
              <form onSubmit={handleNetworkSubmit}>
                <StyledTextField
                  fullWidth
                  variant="outlined"
                  label="URL da Rede"
                  placeholder="http://localhost:80"
                  value={nodeAddress}
                  onChange={(e) => setNodeAddress(e.target.value)}
                  disabled={loading}
                />

                <LoginButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={!nodeAddress.trim() || loading}
                  startIcon={<i className="bx bx-network-chart" />}
                >
                  Conectar à Rede
                </LoginButton>
              </form>
            </FormSection>
          </>
        )}

        {step === 'credentials' && (
          <>
            <NodeInfo>
              <NodeLabel variant="body2">Rede Selecionada</NodeLabel>
              <NodeAddress variant="body1">{nodeAddress}</NodeAddress>
            </NodeInfo>

            <FormSection>
              <SectionTitle variant="h6">
                <SectionIcon className="bx bx-key" />
                Credenciais de Acesso
              </SectionTitle>
              
              <form onSubmit={handleLoginSubmit}>
                <StyledTextField
                  fullWidth
                  variant="outlined"
                  label="Client ID"
                  placeholder="Digite seu Client ID"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  disabled={loading}
                  autoComplete="username"
                />
                
                <StyledTextField
                  fullWidth
                  variant="outlined"
                  type="password"
                  label="Client Secret"
                  placeholder="Digite seu Client Secret"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                />

                <LoginButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={!clientId.trim() || !clientSecret.trim() || loading}
                  startIcon={<i className={loading ? "bx bx-loader-alt bx-spin" : "bx bx-log-in"} />}
                >
                  {loading ? 'Autenticando...' : 'Entrar'}
                </LoginButton>
              </form>

              <ButtonGroup>
                <SecondaryButton
                  variant="outlined"
                  onClick={() => setStep('network')}
                  disabled={loading}
                  startIcon={<i className="bx bx-arrow-back" />}
                >
                  Alterar Rede
                </SecondaryButton>
              </ButtonGroup>
            </FormSection>
          </>
        )}

        <SecurityNote>
          <SecurityIcon className="bx bx-shield-check" />
          <SecurityText variant="body2">
            Conexão criptografada e validação na blockchain
          </SecurityText>
        </SecurityNote>
      </LoginCard>
    </LoginContainer>
  );
});

export default LoginScreen;
